// DJSEM workspace app — the single, platform-agnostic renderer.
//
// This is the whole desktop/web workspace UI: the AppShell + the module router. Individual
// module components live in ./modules/*.tsx (see docs/REACT_COMPONENT_PLAYBOOK.md). It was
// previously authored inside apps/mvp-electron/src/renderer/main.tsx and reached for
// Electron-only globals (window.djsemLive / window.djsemApiBase). It now takes a `DJSEMPlatform`
// so the SAME component runs unchanged on Electron and on the web — the web host passes a web
// platform (no live bridge, API base from env), the Electron host passes its bridge-backed one.
//
// No behaviour changed in the module split: the modules, states, safety copy, and API wiring are
// identical to the original renderer. Only the source of live-health/cache/api-base moved behind
// the adapter, and the module bodies moved into their own files.
//
// @see docs/state/agents/claude/prompts/144-offline-desktop-entitlement-token.md — the offline desktop
// entitlement cache wiring below (task #45, Phase C) that lets this component keep honoring a
// signed-in user's real plan/render-credits when `/api/me` can't be reached.

import { useCallback, useEffect, useState } from "react";
import {
  AppShell, EmptyState, OfflineState, PlanLockedState,
  type DJSEMNavModuleId, type ShellUserContext, type ShellStatusInput, type DJSEMPlanTierUI,
  DJSEMApiClient, useApiClient, useApiResource, badgeCount,
} from "./index";
import type { DJSEMCachedLicense } from "./platform";
import { planLevelToProviderPlanTier } from "@djsem/shared";
import type { DJSEMPlatform, DJSEMLiveHealth } from "./platform";
import { deriveShellOnlineStatus } from "./platform";
import { LiveModule } from "./modules/LiveModule";
import { DashboardModule } from "./modules/DashboardModule";
import { LibraryModule } from "./modules/LibraryModule";
import { PlaylistsModule } from "./modules/PlaylistsModule";
import { CratesModule } from "./modules/CratesModule";
import { SetsModule } from "./modules/SetsModule";
import { VisualsModule } from "./modules/VisualsModule";
import { BrandWorldModule } from "./modules/BrandWorldModule";
import { CreateModule } from "./modules/CreateModule";
import { StemStudioModule } from "./modules/StemStudio";
import { GigsModule } from "./modules/GigsModule";
import { AssetsModule } from "./modules/AssetsModule";
import { ReleasesModule } from "./modules/ReleasesModule";
import { DistributionModule } from "./modules/DistributionModule";
import { CampaignsModule } from "./modules/CampaignsModule";
import { GatedDropsModule } from "./modules/GatedDropsModule";
import { JobsModule } from "./modules/JobsModule";
import { SettingsModule } from "./modules/SettingsModule";
import { AdminModule } from "./modules/AdminModule";
import { FeedbackIntelligenceModule } from "./modules/FeedbackIntelligenceModule";

function ModuleView({ module, user, health, cachedCount, client, liveAvailable, platform, license, onLicenseChanged }: {
  module: DJSEMNavModuleId; user: ShellUserContext; health: DJSEMLiveHealth | null; cachedCount: number; client: DJSEMApiClient; liveAvailable: boolean; platform: DJSEMPlatform;
  license: DJSEMCachedLicense | null; onLicenseChanged: () => void;
}) {
  switch (module) {
    case "live": return <LiveModule health={health} cachedCount={cachedCount} liveAvailable={liveAvailable} platform={platform} />;
    case "dashboard": return <DashboardModule />;
    case "library": return <LibraryModule client={client} platform={platform} />;
    case "playlists": return <PlaylistsModule client={client} />;
    case "crates": return <CratesModule />;
    case "sets": return <SetsModule client={client} />;
    case "visuals": return <VisualsModule />;
    case "brand": return <BrandWorldModule />;
    case "create":
      return user.plan === "free"
        ? <PlanLockedState minPlan="creator" feature="Creative Studio" />
        : <CreateModule client={client} platform={platform} />;
    case "stems":
      return user.plan === "free"
        ? <PlanLockedState minPlan="creator" feature="Stem Studio" />
        : <StemStudioModule client={client} platform={platform} />;
    case "gigs": return <GigsModule client={client} />;
    case "feedback": return <FeedbackIntelligenceModule client={client} />;
    case "assets": return <AssetsModule client={client} />;
    case "releases": return <ReleasesModule />;
    case "distribution":
      return user.plan === "free" || user.plan === "creator"
        ? <PlanLockedState minPlan="creator_pro" feature="Distribution hub" />
        : <DistributionModule />;
    case "campaigns": return <CampaignsModule />;
    case "drops": return <GatedDropsModule />;
    case "jobs": return <JobsModule client={client} />;
    case "marketplace":
      return <EmptyState title="Marketplace" detail="Browse templates and packs. Publishing requires cleared rights." />;
    case "settings": return <SettingsModule user={user} client={client} platform={platform} license={license} onLicenseChanged={onLicenseChanged} />;
    case "admin": case "support": case "moderation":
      return <AdminModule permissions={user.permissions} />;
    default: return <EmptyState title="Not found" />;
  }
}

/**
 * The whole DJSEM workspace app — the single, platform-agnostic renderer shared 1:1 by the
 * Electron desktop app and the web app. Give it a `DJSEMPlatform` and it renders the full
 * shell (nav, top bar, status bar, command palette) plus the module router identically on
 * both hosts; only the platform adapter's behaviour differs between them.
 *
 * Responsibilities beyond rendering the shell:
 * - Resolves the signed-in user's plan tier from `/api/me`, defaulting to `"free"` (fail-safe)
 *   while loading or unauthenticated.
 * - Wires the offline desktop entitlement cache (task #45 Phase C — see the file-level `@see`):
 *   refreshes the cached signed token whenever `/api/me` succeeds, and falls back to the cached
 *   token's plan (instead of demoting to free) when `/api/me` fails while offline. This is a
 *   no-op on web, where `platform.offlineEntitlement` is a null-object.
 * - Derives cloud-online / safe-mode status via `deriveShellOnlineStatus`, which correctly
 *   treats "no live bridge" (web) as online rather than offline.
 * - Fetches notification/task counts for the shell's badge counters.
 * @param platform - The host's platform adapter (`createElectronPlatform()` or `createWebPlatform()`).
 */
export function DJSEMWorkspaceApp({ platform }: { platform: DJSEMPlatform }) {
  const [health, setHealth] = useState<DJSEMLiveHealth | null>(null);
  const [cachedCount, setCachedCount] = useState<number>(0);
  const [module, setModule] = useState<DJSEMNavModuleId>("dashboard");
  const liveAvailable = platform.live.available;

  useEffect(() => {
    const live = platform.live;
    void live.getHealth().then(setHealth);
    void live.listCached().then((c) => setCachedCount(c.length));
    return live.onHealth(setHealth);
  }, [platform]);

  const client = useApiClient(platform.apiBase);

  // Prompt 03B wiring: real plan comes from /api/me (backed by the users table's plan_level
  // column — see STATE_HEAD.md for the "until auth lands" placeholder this replaces). While
  // loading or if unauthenticated, default to the lowest tier ("free") — fail-safe, never
  // over-grants access before the real plan is known. Permission STRINGS (admin/support nav)
  // remain a separate, still-open gap — AuthUser only has a role, not a permissions array;
  // out of scope for this pass (plan-tier + credit-balance only).
  const meRes = useApiResource(() => client.me(), []);

  // Task #45 Phase C: offline desktop entitlement cache. When /api/me succeeds, refresh the
  // signed token so it's ready if the connection drops mid-session; when /api/me fails
  // (offline), fall back to the cached token INSTEAD of defaulting to "free" — a DJ using the
  // desktop Studio at a venue with no internet shouldn't lose their paid plan mid-show. No-op
  // on web (platform.offlineEntitlement is a null-object there).
  const [cachedEntitlement, setCachedEntitlement] = useState<{ planLevel: string; renderCreditsRemaining: number } | null>(null);
  useEffect(() => {
    if (meRes.view.state === "ready" && platform.offlineEntitlement.available) {
      void client.getEntitlementToken().then((r) => {
        if (r.ok && r.data.token) void platform.offlineEntitlement.cacheToken(r.data.token);
      });
    }
    if (meRes.view.state === "offline" && platform.offlineEntitlement.available) {
      void platform.offlineEntitlement.getCached().then(setCachedEntitlement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meRes.view.state, platform]);

  // Task #45 Phase E: perpetual desktop license. Desktop replaces recurring subscription
  // billing entirely — once a license is activated, it is the PRIMARY (not fallback) source of
  // desktop feature-tier entitlement, so /api/me's account plan is deliberately NOT consulted
  // for `resolvedPlan` on desktop below. No license cached/valid → "free", full stop, even if
  // the signed-in account happens to carry a paid plan (that plan only matters for CLOUD
  // features — e.g. AI render compute — which stay account/subscription-gated regardless of
  // platform). No-op on web (platform.license is a null-object there; resolvedPlan below falls
  // through to the unchanged /api/me + Phase-C-cache logic exactly as before this pass).
  const [license, setLicense] = useState<DJSEMCachedLicense | null>(null);
  // Exposed to SettingsModule's LicensePanel too (as `onLicenseChanged`) so activating a new key,
  // a manual revocation recheck, or removing this machine's license all re-run the same load —
  // one source of truth for "what does this machine's cache currently say" instead of each screen
  // maintaining its own copy.
  const refreshLicense = useCallback(async () => {
    if (!platform.license.available) { setLicense(null); return; }
    const cached = await platform.license.getCached();
    if (!cached) { setLicense(null); return; }
    setLicense(cached);
    // Best-effort online revocation check (see perpetualLicenseToken.ts for why this can't be
    // real-time) — a failed/offline request leaves the cache and tier untouched.
    const res = await client.getLicenseRevocationStatus(cached.licenseId);
    if (res.ok && res.data.status !== "paid") {
      await platform.license.clearCached();
      setLicense(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);
  useEffect(() => { void refreshLicense(); }, [refreshLicense]);

  const resolvedPlan: DJSEMPlanTierUI = platform.license.available
    ? (license ? planLevelToProviderPlanTier(license.tier as never) : "free")
    : meRes.view.data?.user
      ? planLevelToProviderPlanTier(meRes.view.data.user.planLevel as never)
      : cachedEntitlement
        ? planLevelToProviderPlanTier(cachedEntitlement.planLevel as never)
        : "free";
  const user: ShellUserContext = { plan: resolvedPlan, permissions: [] };

  const notifications = useApiResource(() => client.listNotifications(), []);
  const tasks = useApiResource(() => client.listTasks(), []);

  // Online status must reflect CLOUD/API reachability, not the desktop live-bridge. On web there
  // is no live bridge (health stays null) but the cloud API is reachable — so a null health must
  // NOT read as "offline", or the browser app would look permanently broken. Rule lives in the
  // pure, tested `deriveShellOnlineStatus` helper.
  const { cloudOnline, safeModeActive } = deriveShellOnlineStatus({ liveAvailable, health });

  const status: ShellStatusInput = {
    online: cloudOnline,
    cachedPackageCount: cachedCount,
    syncQueueLength: 0,
    activeJobCount: 0,
    rightsWarningCount: 0,
    liveState: "idle",
    safeModeActive,
    version: "0.10.0",
    channel: "dev",
  };

  return (
    <AppShell
      user={user}
      status={status}
      activeModule={module}
      onNavigate={setModule}
      organisationLabel="Personal"
      projectLabel={cachedCount > 0 ? `Local cache (${cachedCount})` : undefined}
      notificationCount={badgeCount(notifications)}
      taskCount={badgeCount(tasks)}
    >
      {!status.online && module !== "live" ? <div style={{ marginBottom: 12 }}><OfflineState /></div> : null}
      <ModuleView module={module} user={user} health={health} cachedCount={cachedCount} client={client} liveAvailable={liveAvailable} platform={platform} license={license} onLicenseChanged={() => void refreshLicense()} />
    </AppShell>
  );
}
